import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { Box, Checkbox, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	NUMBER_CELL_CLASS,
	NUMBER_CELL_ROOMY_CLASS,
	ONE_LINE_CELL_CLASS,
} from "@/controls/ag-grid/OneLineCells";
import type { Recipe } from "../models/Recipe";
import { activeColumnDef } from "@/controls/ag-grid/components/ActiveColumn";

const numberCell = [
	ONE_LINE_CELL_CLASS,
	NUMBER_CELL_CLASS,
	NUMBER_CELL_ROOMY_CLASS,
];

interface UseTableDataProps {
	onDelete: (recipe: Recipe) => void;
}

export const useTableData = ({ onDelete }: UseTableDataProps) => {
	const columnDefs: ColDef<Recipe>[] = [
		{
			field: "Name",
			headerName: "Name",
			flex: 1.6,
			minWidth: 140,
			sortable: true,
			filter: true,
			tooltipField: "Name",
			cellClass: ONE_LINE_CELL_CLASS,
		},
		{
			field: "ProductTypeName",
			headerName: "Product Type",
			flex: 1,
			minWidth: 100,
			sortable: true,
			filter: true,
			cellClass: ONE_LINE_CELL_CLASS,
		},
		{
			field: "ProductSizeName",
			headerName: "Product",
			flex: 1.2,
			minWidth: 120,
			sortable: true,
			filter: true,
			tooltipField: "ProductSizeName",
			cellClass: ONE_LINE_CELL_CLASS,
		},
		{
			field: "LineSpeed",
			headerName: "Line Speed",
			width: 90,
			cellClass: numberCell,
			valueFormatter: (params: { value: number | null }) =>
				params.value != null ? String(params.value) : "",
		},
		{
			field: "StartTemperature",
			headerName: "Start Temp",
			width: 90,
			cellClass: numberCell,
			valueFormatter: (params: { value: number | null }) =>
				params.value != null ? String(params.value) : "",
		},
		{
			field: "EndTemperature",
			headerName: "End Temp",
			width: 90,
			cellClass: numberCell,
			valueFormatter: (params: { value: number | null }) =>
				params.value != null ? String(params.value) : "",
		},
		{
			field: "EnableComparison",
			headerName: "Compare",
			width: 85,
			sortable: false,
			filter: false,
			cellRenderer: (params: ICellRendererParams<Recipe>) => {
				if (!params.data) return null;
				return (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<Checkbox
							checked={params.data.EnableComparison}
							size="small"
							disabled
							sx={{ p: 0 }}
						/>
					</Box>
				);
			},
		},
		activeColumnDef<Recipe>(),
		{
			headerName: "",
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			sortable: false,
			filter: false,
			resizable: false,
			suppressSizeToFit: true,
			cellRenderer: (params: ICellRendererParams<Recipe>) => {
				if (!params.data) return null;
				return (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<Tooltip title="Delete">
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									onDelete(params.data as Recipe);
								}}
								color="error"
								sx={{ p: 0.25 }}
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				);
			},
		},
	];

	return { columnDefs };
};
